// UI helper — confirm / alert / toast / loading 統一入口
// 取代散落的 confirm() / alert() / 自製 backdrop modal。
//
// API:
//   await ui.confirm(message, opts)            // returns boolean
//     opts: { title, okText, cancelText, type }
//   await ui.alert(message, opts)              // returns void
//     opts: { title, okText, type }
//   ui.toast(message, type='success', duration=3000)
//     type: 'success' | 'error' | 'warn' | 'info'
//
// 樣式全站統一（2026-10-22）：提示框與對話框共用同一組語氣與顏色，見 Common/uiTone.js 與 UiContainer.vue。
// 對話框的 opts.type 可省略——沒給時確認框是「請確認」，訊息框從標題＋內文猜（uiTone.modalTone）。
//   ui.loading.show() / ui.loading.hide()      // 全域 loading overlay (可巢狀)
//
// 用法:
//   import { ui } from '@/Common/ui.js'
//   if (await ui.confirm('確定刪除?')) { ... }
//   ui.toast('已儲存', 'success')

import { reactive } from 'vue'

const state = reactive({
  modal: {
    visible: false,
    title: '提示',
    message: '',
    okText: '確定',
    cancelText: '取消',
    showCancel: true,
    type: null,
    resolve: null,
  },
  toasts: [],
  loadingCount: 0,
})

let toastSeq = 0

function showModal(message, opts, showCancel) {
  return new Promise((resolve) => {
    state.modal.visible = true
    state.modal.title = opts?.title ?? (showCancel ? '確認' : '提示')
    state.modal.message = message ?? ''
    state.modal.okText = opts?.okText ?? '確定'
    state.modal.cancelText = opts?.cancelText ?? '取消'
    state.modal.showCancel = showCancel
    state.modal.type = opts?.type ?? null
    state.modal.resolve = resolve
  })
}

function resolveModal(result) {
  const resolve = state.modal.resolve
  state.modal.visible = false
  state.modal.resolve = null
  if (resolve) resolve(result)
}

export const ui = {
  state,  // expose for UiContainer.vue
  resolveModal,

  confirm(message, opts) {
    return showModal(message, opts, true)
  },

  alert(message, opts) {
    return showModal(message, opts, false).then(() => undefined)
  },

  toast(message, type = 'success', duration = 3000) {
    const id = ++toastSeq
    state.toasts.push({ id, message: message ?? '', type, duration })
    if (duration > 0) {
      setTimeout(() => ui.dismissToast(id), duration)
    }
    return id
  },

  dismissToast(id) {
    const idx = state.toasts.findIndex(t => t.id === id)
    if (idx >= 0) state.toasts.splice(idx, 1)
  },

  loading: {
    show() { state.loadingCount++ },
    hide() { state.loadingCount = Math.max(0, state.loadingCount - 1) },
    reset() { state.loadingCount = 0 },
  },
}

// 暴露到 window 以便 Razor inline script 呼叫
if (typeof window !== 'undefined') {
  window.ui = ui
}
